import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { Route, Switch } from 'react-router'
import APIKeysInfo from 'src/app/APIProduct/APIKeys/APIKeys'
import ProfileDelete from 'src/app/ProfileDelete/ProfileDelete'
import ProfileInfo from 'src/app/ProfileInfo/ProfileInfo'
import ProfileNotifications from 'src/app/ProfileNotifications/ProfileNotifications'
import ProfileOverview from 'src/app/ProfileOverview/ProfileOverview'
import ProfileSecurity from 'src/app/ProfileSecurity/ProfileSecurity'
import BackgroundPage from 'src/components/BackgroundPage/BackgroundPage'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import TitleHeader from 'src/components/TitleHeader/TitleHeader'
import Topnav from 'src/components/Topnav/Topnav'
import { useTranslate } from 'src/localization/Trns'
import { Rdrct } from 'src/navigation/Lnk'
import { profileRouteTree } from 'src/navigation/profileRouteTree'
import {
  apiKeysProfileUrl,
  profileDeleteUrl,
  profileInfoUrl,
  profileNotificationsUrl,
  profileOverviewUrl,
  profileSecurityUrl,
  profileUrl,
} from 'src/navigation/urls'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

/**
 * ProfileFamilyPage — route-level page for the Profile ("Profile Settings")
 * family routes (EX-39748). Migrated from the legacy `src/app/Profile/Profile`
 * shell into the flat route registry; existing URLs are unchanged.
 *
 * DELIBERATE DEVIATION FROM BUDGETS (documented in the PR), identical in spirit
 * to AccountsFamilyPage: the Profile family is NOT an in-page tabbed surface —
 * there is no `Subnav` tab bar on `/profile`. Navigation between the profile
 * sub-pages is driven by the persistent SETTINGS SIDENAV
 * (`src/navigation/sidenav/useSettingsNavItems` + `SidenavContentType.Settings`),
 * whose "Profile Settings" group highlights on the profile `AppLocation`s. So
 * this page PRESERVES the legacy no-tab-bar rendering (a "Profile Settings"
 * title + a nested `<Switch>`) rather than introducing a `FamilyLayout` tab bar
 * that would change UX. The family registers one multi-path flat gate entry
 * (profileFamilyGatePaths) so the page stays mounted across sub-pages — see
 * flatArchitectureRoutes.tsx.
 *
 * There is no `/:id` entity in this family — every route is a static
 * `/profile/*` path. Tab content components stay routing-free; this page is the
 * only routing boundary.
 *
 * The legacy profile OVERLAY routes (`/profile/photo`,
 * `/profile/security/changepassword`, `/profile/security/devices`,
 * `/profile/screening`) are intentionally left in the legacy overlay registry
 * (authedPopupAppRoute) — they render on top of this page's background location,
 * exactly as they did before, so they are out of this flat-migration lane.
 */
const ProfileFamilyPage: React.FC = () => {
  const translate = useTranslate()
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const appName = useSelector(selectAppName)

  return (
    <BackgroundPage>
      <Helmet>
        <title>{`Profile Settings | ${appName}`}</title>
        <meta
          name="description"
          content={`Profile Settings page of ${appName}`}
        />
      </Helmet>
      <Topnav
        titleElements={{
          scrollContent: (
            <span
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              {translate('Profile Settings')}
            </span>
          ),
        }}
      />
      <TitleHeader
        title={
          <span data-testid="profileSettingsHeading">
            {translate('Profile Settings')}
          </span>
        }
      />
      <Switch location={calculatedBackgroundLocation}>
        {/* apikeys is a 3-segment static path nested under overview; with exact
            routes it never collides with /profile/overview. */}
        <Route exact path={apiKeysProfileUrl()}>
          <APIKeysInfo />
        </Route>
        <Route exact path={profileOverviewUrl()}>
          <ProfileOverview />
        </Route>
        <Route exact path={profileInfoUrl()}>
          <ProfileInfo />
        </Route>
        <Route exact path={profileNotificationsUrl()}>
          <ProfileNotifications />
        </Route>
        <Route exact path={profileSecurityUrl()}>
          <ProfileSecurity />
        </Route>
        <Route exact path={profileDeleteUrl()}>
          <ProfileDelete />
        </Route>
        <Route exact path={profileUrl()}>
          <Rdrct to={profileRouteTree.family.redirectTo} />
        </Route>
        <Route path={'/'}>
          <NotFoundBackgroundPageContent />
        </Route>
      </Switch>
    </BackgroundPage>
  )
}

export default ProfileFamilyPage
